import React, { createContext, ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from "react";

/**
 * Viewport width buckets for compact layout decisions.
 * Boundaries are inclusive on the upper bound for XXS–M.
 */
export enum ScreenWidthSize {
    XXS = "XXS", // <= 260px
    XS = "XS", // <= 300px
    S = "S", // <= 340px
    M = "M", // <= 380px
    L = "L", // > 380px
}

export const SCREEN_WIDTH_BREAKPOINTS = {
    XXS: 260,
    XS: 300,
    S: 359,
    M: 380,
} as const;

export function getScreenWidthSize(width: number): ScreenWidthSize {
    if (width <= SCREEN_WIDTH_BREAKPOINTS.XXS) {
        return ScreenWidthSize.XXS;
    }
    if (width <= SCREEN_WIDTH_BREAKPOINTS.XS) {
        return ScreenWidthSize.XS;
    }
    if (width <= SCREEN_WIDTH_BREAKPOINTS.S) {
        return ScreenWidthSize.S;
    }
    if (width <= SCREEN_WIDTH_BREAKPOINTS.M) {
        return ScreenWidthSize.M;
    }
    return ScreenWidthSize.L;
}

/** True for viewport buckets at or below 340px (XXS, XS, S). */
export function isCompactScreenWidth(size: ScreenWidthSize): boolean {
    return size === ScreenWidthSize.XXS || size === ScreenWidthSize.XS || size === ScreenWidthSize.S;
}

export interface ScreenWidthContextValue {
    screenWidthSize: ScreenWidthSize;
}

const DEFAULT_VALUE: ScreenWidthContextValue = {
    screenWidthSize: ScreenWidthSize.L,
};

export const ScreenWidthContext = createContext<ScreenWidthContextValue>(DEFAULT_VALUE);

function getInitialScreenWidthSize(): ScreenWidthSize {
    if (typeof window === "undefined") {
        return ScreenWidthSize.L;
    }
    return getScreenWidthSize(window.innerWidth);
}

/**
 * Resize-aware viewport bucket for Quick Checkout footer layout.
 *
 * Use case: AIP / QC turbo footer needs compact vs roomy pay-button / method chrome
 * (hide item-count on narrow widths, clamp payment-method width). Only the QC footer
 * tree should mount this provider — do not wrap the full cart / checkout tree.
 *
 * Consumers call `useScreenWidthSize` / `isCompactScreenWidth` under this provider
 * (e.g. QC turbo method widget + pay button). Outside the provider they fall back to L.
 */
export function ScreenWidthProvider(props: { children: ReactNode }): ReactElement {
    const [screenWidthSize, setScreenWidthSize] = useState<ScreenWidthSize>(getInitialScreenWidthSize);

    useEffect(() => {
        const updateScreenWidthSize = (): void => {
            const nextSize = getScreenWidthSize(window.innerWidth);
            setScreenWidthSize((prevSize) => (prevSize === nextSize ? prevSize : nextSize));
        };

        updateScreenWidthSize();
        window.addEventListener("resize", updateScreenWidthSize);

        return () => {
            window.removeEventListener("resize", updateScreenWidthSize);
        };
    }, []);

    const contextValue = useMemo(
        () => ({
            screenWidthSize,
        }),
        [screenWidthSize],
    );

    return <ScreenWidthContext.Provider value={contextValue}>{props.children}</ScreenWidthContext.Provider>;
}

export function useScreenWidthSize(): ScreenWidthSize {
    return useContext(ScreenWidthContext).screenWidthSize;
}

if (__DEV__) {
    ScreenWidthContext.displayName = "ScreenWidthContext";
}
